import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import { 
  Globe, 
  Settings, 
  Wifi, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Image,
  Webhook,
  Edit,
  Loader2
} from 'lucide-react';
import { EnvironmentVariablesEditor } from './EnvironmentVariablesEditor';
import { ProxyConfigError } from './ProxyConfigError';
import { useProxyServiceStatus } from '@/hooks/useProxyServiceStatus';
import { useProxyConfig } from '@/contexts/ProxyConfigContext';

export const ProxySettings: React.FC = () => {
  const { 
    serviceStatuses, 
    checking, 
    checkAllServices, 
    checkSingleService,
    refreshStatuses,
    configError,
    initialized
  } = useProxyServiceStatus();
  
  const { forceReload, loading: configLoading } = useProxyConfig();

  // Обновляем статусы при изменении настроек
  const handleConfigUpdate = async () => {
    console.log('🔄 Начинаем обновление конфигурации...');
    toast.info('Применение настроек...');
    
    try {
      await forceReload();
      await refreshStatuses();
      
      // Небольшая задержка для загрузки новой конфигурации
      setTimeout(async () => {
        await checkAllServices();
        toast.success('Настройки успешно применены');
      }, 2000);
    } catch (error) {
      console.error('Ошибка при обновлении конфигурации:', error);
      toast.error('Ошибка при применении настроек');
    }
  };

  // Показываем индикатор загрузки если конфигурация или статусы еще не инициализированы
  if (configLoading || !initialized) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Globe className="h-5 w-5 mr-2 text-tuktuk-teal" />
              Настройки прокси для внешних сервисов
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-6 w-6 animate-spin text-tuktuk-teal" />
                <span className="text-lg">Загрузка конфигурации прокси...</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusIcon = (status: typeof serviceStatuses[0]) => {
    if (status.checking) {
      return <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />;
    }
    
    if (status.healthy) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  };

  const getStatusBadge = (status: typeof serviceStatuses[0]) => {
    if (status.checking) {
      return <Badge variant="secondary">Проверяется...</Badge>;
    }
    
    if (status.healthy) {
      return <Badge variant="default" className="bg-green-500">Доступен</Badge>;
    }
    
    return <Badge variant="destructive">Недоступен</Badge>;
  };

  const getProxyBadge = (enabled: boolean) => {
    return enabled 
      ? <Badge variant="outline" className="text-blue-600"><Shield className="h-3 w-3 mr-1" />Прокси</Badge>
      : <Badge variant="outline" className="text-gray-500">Прямое соединение</Badge>;
  };

  const handleSingleServiceCheck = async (serviceName: string) => {
    console.log(`🔍 Проверяем сервис: ${serviceName}`);
    const result = await checkSingleService(serviceName);
    toast.success(`${serviceName}: ${result ? 'Доступен' : 'Недоступен'}`);
  };

  const handleAllServicesCheck = async () => {
    console.log('🔍 Проверяем все сервисы...');
    const results = await checkAllServices();
    const healthyCount = results.filter(s => s.healthy).length;
    toast.success(`Проверка завершена: ${healthyCount}/${results.length} сервисов доступны`);
  };

  const handleForceRefresh = async () => {
    console.log('🔄 Принудительное обновление...');
    toast.info('Обновление конфигурации...');
    
    try {
      await refreshStatuses();
      setTimeout(async () => {
        await checkAllServices();
        toast.success('Конфигурация обновлена');
      }, 2000);
    } catch (error) {
      console.error('Ошибка при обновлении:', error);
      toast.error('Ошибка при обновлении конфигурации');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Globe className="h-5 w-5 mr-2 text-tuktuk-teal" />
              Настройки прокси для внешних сервисов
            </CardTitle>
            <Button
              onClick={handleForceRefresh}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Обновить все
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Показываем ошибку конфигурации, если она есть */}
          {configError && (
            <ProxyConfigError 
              error={configError}
              onRetry={forceReload}
              loading={configLoading}
            />
          )}

          <Tabs defaultValue="status" className="space-y-4">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="status" className="flex items-center gap-2">
                <Wifi className="h-4 w-4" />
                <span>Статус сервисов</span>
              </TabsTrigger>
              <TabsTrigger value="editor" className="flex items-center gap-2">
                <Edit className="h-4 w-4" />
                <span>Редактор переменных</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="status" className="space-y-4">
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  Эти настройки позволяют настроить проксирование запросов к внешним сервисам 
                  для обхода блокировок. Настройки применяются сразу после сохранения.
                  {configError && (
                    <span className="block mt-2 text-amber-600">
                      ⚠️ Используется резервная конфигурация из-за проблем с загрузкой настроек.
                    </span>
                  )}
                </AlertDescription>
              </Alert>

              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Статус сервисов</h3>
                <Button 
                  onClick={handleAllServicesCheck} 
                  disabled={checking}
                  variant="outline"
                  size="sm"
                >
                  {checking ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Проверяется...
                    </>
                  ) : (
                    <>
                      <Wifi className="h-4 w-4 mr-2" />
                      Проверить все
                    </>
                  )}
                </Button>
              </div>

              {serviceStatuses.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin text-tuktuk-teal" />
                    <span>Инициализация статусов сервисов...</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {serviceStatuses.map((status) => (
                    <div key={status.service} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {status.service === 'supabase' && <Globe className="h-4 w-4" />}
                        {status.service === 'supabaseStorage' && <Image className="h-4 w-4" />}
                        {status.service === 'googleMaps' && <Globe className="h-4 w-4" />}
                        {status.service === 'geocoding' && <Globe className="h-4 w-4" />}
                        {status.service === 'makeWebhooks' && <Webhook className="h-4 w-4" />}
                        {getStatusIcon(status)}
                        <div>
                          <p className="font-medium">{status.name}</p>
                          <p className="text-sm text-muted-foreground">{status.description}</p>
                          <p className="text-xs text-muted-foreground">{status.url}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {getProxyBadge(status.proxyEnabled)}
                        {getStatusBadge(status)}
                        <Button
                          onClick={() => handleSingleServiceCheck(status.service)}
                          disabled={status.checking}
                          variant="ghost"
                          size="sm"
                        >
                          {status.checking ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Переменные окружения для настройки прокси</h3>
                
                <Alert>
                  <Settings className="h-4 w-4" />
                  <AlertDescription>
                    Для настройки прокси используйте вкладку "Редактор переменных" выше или добавьте следующие переменные в ваш .env файл:
                  </AlertDescription>
                </Alert>

                <div className="bg-muted p-4 rounded-lg font-mono text-sm space-y-1">
                  <div># Supabase API прокси</div>
                  <div>VITE_SUPABASE_PROXY_ENABLED=true</div>
                  <div>VITE_SUPABASE_PROXY_URL=https://your-proxy.com/supabase</div>
                  <div>VITE_SUPABASE_PROXY_TIMEOUT=30000</div>
                  <div></div>
                  <div># Supabase Storage прокси</div>
                  <div>VITE_SUPABASE_STORAGE_PROXY_ENABLED=true</div>
                  <div>VITE_SUPABASE_STORAGE_PROXY_URL=https://your-proxy.com/storage</div>
                  <div>VITE_SUPABASE_PROXY_TIMEOUT=30000</div>
                  <div></div>
                  <div># Google Maps прокси</div>
                  <div>VITE_GOOGLE_MAPS_PROXY_ENABLED=true</div>
                  <div>VITE_GOOGLE_MAPS_PROXY_URL=https://your-proxy.com/gmaps</div>
                  <div>VITE_GOOGLE_MAPS_PROXY_TIMEOUT=15000</div>
                  <div></div>
                  <div># Geocoding прокси</div>
                  <div>VITE_GEOCODING_PROXY_ENABLED=true</div>
                  <div>VITE_GEOCODING_PROXY_URL=https://your-proxy.com/geocoding</div>
                  <div>VITE_GEOCODING_PROXY_TIMEOUT=15000</div>
                  <div></div>
                  <div># Make.com Webhooks прокси</div>  
                  <div>VITE_MAKE_WEBHOOKS_PROXY_ENABLED=true</div>
                  <div>VITE_MAKE_WEBHOOKS_PROXY_URL=https://your-proxy.com/make</div>
                  <div>VITE_MAKE_WEBHOOKS_PROXY_TIMEOUT=10000</div>
                  <div></div>
                  <div># Общие webhooks прокси</div>
                  <div>VITE_WEBHOOKS_PROXY_ENABLED=true</div>
                  <div>VITE_WEBHOOKS_PROXY_URL=https://your-proxy.com/webhooks</div>
                  <div>VITE_WEBHOOKS_PROXY_TIMEOUT=10000</div>
                </div>

                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Настройки применяются динамически без перезапуска приложения. Используйте кнопку "Обновить все" для принудительного обновления конфигурации.
                  </AlertDescription>
                </Alert>
              </div>
            </TabsContent>

            <TabsContent value="editor">
              <EnvironmentVariablesEditor />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
